<template>
  <!-- Edit company dialog -->
  <EditCompanyDialog
    v-if="currentCompany"
    v-model:visible="confirmEditDialogVisible"
    :company="currentCompany"
    @companyUpdated="handleCompanyUpdated" />
  <!-- Confirm delete company -->
  <DeleteConfirmDialog
    v-require-permission="CompanyAccessRole.OWNER"
    v-if="currentCompany"
    v-model:visible="confirmDialogVisible"
    :title="i18n.t('deleteCompany')"
    :sub-title="
      i18n.t('deleteCompanyOf', {
        company: currentCompany.name,
      })
    "
    @cancel="confirmDialogVisible = false"
    @delete="deleteCompany()" />

  <!-- Companies table -->
  <div class="flex flex-col gap-5 p-4 pb-5 pt-5">
    <div class="w-full">
      <h3>{{ i18n.t('companies') }}</h3>
    </div>
    <SmTable
      :data="companies"
      :columns="companiesColumns"
      :loading="loading"
      key-field="company_id">
      <!-- Company name -->
      <template #name="{ row }"><TextMultiline :text="row.name" /></template>
      <!-- Company role -->
      <template #role="{ row }"
        ><TextMultiline :text="capitalize(i18n.t(row.role))"
      /></template>
      <!-- Last login -->
      <template #last_login="{ row }">
        <TextMultiline
          v-if="row.last_login !== null"
          :text="
            displayDate(new Date(row.last_login!), {
              dateStyle: 'short',
            })
          " />
        <TextMultiline v-else :text="i18n.t('never')" />
      </template>
      <!-- Actions -->
      <template #actions="{ row }">
        <SmDropdown trigger="click">
          <div>
            <SmDropdownItem
              :aria-disabled="!CompanyAccessRole.OWNER"
              :style="{
                cursor: !CompanyAccessRole.OWNER ? 'pointer' : 'default',
              }"
              :disabled="row.role != 'owner'"
              :icon="PencilLine"
              @item-click="openEditDialog(row)">
              {{ i18n.t('editCompany') }}
            </SmDropdownItem>
            <SmDropdownItem
              :aria-disabled="!CompanyAccessRole.OWNER"
              :style="{
                cursor: !CompanyAccessRole.OWNER ? 'pointer' : 'default',
              }"
              :disabled="row.role != 'owner'"
              hover-color="var(--sm-magenta)"
              :icon="Trash"
              @item-click="openDeleteDialog(row)">
              {{ i18n.t('deleteCompany') }}
            </SmDropdownItem>
          </div>
        </SmDropdown>
      </template>
    </SmTable>
  </div>
</template>

<script setup lang="ts">
  import {
    CompaniesService,
    AvailableCompany,
    CompanyAccessRole,
  } from '@/client'

  import { useI18n } from 'vue-i18n'
  import { capitalize } from '@/utils/formatter/formatter'
  import { displayDate } from '@/utils/formatter/date'
  import { Trash, PencilLine } from 'lucide-vue-next'
  import { sendToast } from '../sm/SmNotification'
  import { Column } from '../sm/SmTable.types'
  import { useRouter } from 'vue-router'
  import { useSessionStore } from '@/stores/sessionStore'

  const i18n = useI18n()
  const sessionStore = useSessionStore()
  const router = useRouter()

  const emit = defineEmits(['deletedCompany'])

  const companiesColumns: Column<AvailableCompany>[] = [
    {
      key: 'name',
      label: i18n.t('name'),
      sortable: false,
      width: 1,
    },
    {
      label: i18n.t('role'),
      key: 'role',
      sortable: false,
      width: 1,
    },
    {
      label: i18n.t('lastLogin'),
      key: 'last_login',
      sortable: false,
      width: 1,
    },
    {
      label: '',
      key: 'actions',
      width: '40px',
      sortable: false,
    },
  ]

  const loading = ref(false)
  const companies = ref<AvailableCompany[]>([])

  async function getCompanies() {
    loading.value = true
    const companies = await CompaniesService.getAvailableCompaniesCompaniesGet(
      {}
    )

    loading.value = false

    return companies
  }

  async function handleCompanyLoad() {
    companies.value = await getCompanies()
  }

  const currentCompany = ref<AvailableCompany | null>(null)
  const confirmDialogVisible = ref(false)
  const confirmEditDialogVisible = ref(false)
  const error = ref<{ message: string; visible: boolean } | null>(null)
  const activeCompany = sessionStore.currentCompany?.company_id

  // Delete company
  async function deleteCompany() {
    if (!currentCompany.value) return

    try {
      await CompaniesService.deleteCompanyCompaniesCompanyIdDelete({
        companyId: currentCompany.value.company_id,
      })

      sendToast(i18n.t('companyDeleted'), undefined, 'success')
      companies.value = companies.value.filter(
        (c) => c.company_id !== currentCompany.value!.company_id
      )

      if (currentCompany.value.company_id === activeCompany) {
        emit('deletedCompany')
        router.push({ path: '/login/company' })
      }

      currentCompany.value = null
    } catch {
      error.value = {
        message: i18n.t('errorDeletingCompany'),
        visible: true,
      }
    }
  }

  function openDeleteDialog(company: AvailableCompany) {
    currentCompany.value = { ...company }
    confirmDialogVisible.value = true
  }

  // Edit company
  function openEditDialog(company: AvailableCompany) {
    currentCompany.value = { ...company }
    confirmEditDialogVisible.value = true
  }

  function handleCompanyUpdated(updatedCompany: AvailableCompany) {
    const index = companies.value.findIndex(
      (c) => c.company_id === updatedCompany.company_id
    )

    if (index !== -1) {
      companies.value[index] = updatedCompany
    }

    currentCompany.value = null
    confirmEditDialogVisible.value = false
  }

  onMounted(() => {
    handleCompanyLoad()
  })
</script>
